import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from './Modal';
import { RotateCw, ZoomIn, ZoomOut } from 'lucide-react';

/**
 * 프로필 사진 편집 — 정사각 크롭 + 회전 + 사진 필터.
 *
 * 미리보기와 저장이 같은 renderTo()를 쓴다. 예전 캡처 오버레이(ScreenCaptureOverlay)처럼
 * 화면 좌표를 원본 픽셀로 되돌리는 방식 대신, 캔버스 하나에 변환을 직접 걸고 저장 때는
 * 해상도 비율(k)만 곱한다 — 보이는 것과 저장되는 것이 어긋날 여지를 없앤다.
 *
 * 서버는 이 결과물을 가공하지 않는다(런타임 sharp 없음). 여기서 나온 256px JPEG이 그대로 저장된다.
 */

/** 미리보기 캔버스의 내부 해상도. 모든 좌표(offset)는 표시 크기가 아니라 이 기준이다. */
const VIEW = 320;
/** 저장 해상도. 아바타는 최대 64px로 렌더되지만 3배 DPI 화면과 라이트박스를 감안해 256px. */
const OUTPUT = 256;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

interface FilterPreset {
  id: string;
  label: string;
  /** Canvas 2D의 filter 문자열 */
  css: string;
  /** 색상 양자화 레벨 — 지정하면 drawImage 뒤에 픽셀 단위로 한 번 더 처리한다 */
  posterize?: number;
}

const FILTERS: FilterPreset[] = [
  { id: 'none', label: '원본', css: 'none' },
  { id: 'mono', label: '흑백', css: 'grayscale(1) contrast(1.06)' },
  { id: 'sepia', label: '세피아', css: 'sepia(0.65) saturate(1.2) contrast(1.05)' },
  { id: 'vivid', label: '선명', css: 'saturate(1.45) contrast(1.15)' },
  { id: 'bright', label: '밝게', css: 'brightness(1.12) saturate(1.05)' },
  // 색 수를 줄이고 대비를 올려 셀 애니메이션처럼 보이게 한다(외부 AI 변환 없이 캔버스만 쓴다)
  { id: 'toon', label: '만화풍', css: 'saturate(1.6) contrast(1.25)', posterize: 5 },
];

/**
 * 캔버스 필터 지원 여부. iOS Safari 15.4 미만은 ctx.filter를 무시하는데, 그대로 두면
 * 칩을 눌러도 아무 일이 없어 고장으로 보인다 — 미지원이면 필터 UI 자체를 감춘다.
 */
const SUPPORTS_CANVAS_FILTER = (() => {
  try {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return false;
    ctx.filter = 'grayscale(1)';
    return ctx.filter === 'grayscale(1)';
  } catch {
    return false;
  }
})();

/** 색상 양자화 — 채널값을 levels 단계로 반올림해 색 수를 줄인다 */
function applyPosterize(ctx: CanvasRenderingContext2D, size: number, levels: number): void {
  try {
    const image = ctx.getImageData(0, 0, size, size);
    const px = image.data;
    const step = 255 / (levels - 1);
    for (let i = 0; i < px.length; i += 4) {
      px[i] = Math.round(px[i] / step) * step;
      px[i + 1] = Math.round(px[i + 1] / step) * step;
      px[i + 2] = Math.round(px[i + 2] / step) * step;
    }
    ctx.putImageData(image, 0, 0);
  } catch {
    // getImageData는 오염된 캔버스에서만 throw한다(여기선 같은 origin의 blob이라 발생하지 않음)
  }
}

const clampZoom = (z: number): number => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

interface AvatarCropModalProps {
  isOpen: boolean;
  file: File | null;
  onCancel: () => void;
  onConfirm: (blob: Blob) => void;
}

export const AvatarCropModal: React.FC<AvatarCropModalProps> = ({ isOpen, file, onCancel, onConfirm }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const offsetRef = useRef({ x: 0, y: 0 });
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchStartRef = useRef<{ dist: number; zoom: number } | null>(null);

  const [imgReady, setImgReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [filterId, setFilterId] = useState('none');

  const filter = useMemo(() => FILTERS.find(f => f.id === filterId) ?? FILTERS[0], [filterId]);

  /** 이미지가 뷰포트를 빈틈없이 덮는 최소 배율(cover). zoom 1이 곧 이 상태다. */
  const baseScale = useCallback(() => {
    const img = imgRef.current;
    if (!img) return 1;
    const rotated = rotation % 180 !== 0;
    const w = rotated ? img.naturalHeight : img.naturalWidth;
    const h = rotated ? img.naturalWidth : img.naturalHeight;
    if (!w || !h) return 1;
    return Math.max(VIEW / w, VIEW / h);
  }, [rotation]);

  /** 이미지를 끌어도 원형 안쪽에 빈 공간이 생기지 않도록 이동 범위를 제한한다 */
  const clampOffset = useCallback(
    (x: number, y: number, z: number) => {
      const img = imgRef.current;
      if (!img) return { x: 0, y: 0 };
      const s = baseScale() * z;
      const rotated = rotation % 180 !== 0;
      const dw = (rotated ? img.naturalHeight : img.naturalWidth) * s;
      const dh = (rotated ? img.naturalWidth : img.naturalHeight) * s;
      const maxX = Math.max(0, (dw - VIEW) / 2);
      const maxY = Math.max(0, (dh - VIEW) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, x)),
        y: Math.min(maxY, Math.max(-maxY, y)),
      };
    },
    [baseScale, rotation]
  );

  const renderTo = useCallback(
    (ctx: CanvasRenderingContext2D, size: number) => {
      const img = imgRef.current;
      if (!img) return;
      const k = size / VIEW;

      ctx.save();
      ctx.clearRect(0, 0, size, size);
      // 투명 PNG를 JPEG로 저장하면 투명 부분이 검게 나온다 — 흰 바탕을 먼저 깐다
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      if (SUPPORTS_CANVAS_FILTER) ctx.filter = filter.css;
      ctx.translate(size / 2 + offsetRef.current.x * k, size / 2 + offsetRef.current.y * k);
      ctx.rotate((rotation * Math.PI) / 180);
      const s = baseScale() * zoom * k;
      ctx.scale(s, s);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      ctx.restore();

      if (filter.posterize) applyPosterize(ctx, size, filter.posterize);
    },
    [filter, rotation, zoom, baseScale]
  );

  const draw = useCallback(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) renderTo(ctx, VIEW);
  }, [renderTo]);

  // 파일 로드. <img>를 쓰면 EXIF 방향이 브라우저에서 이미 적용된 상태로 들어온다
  // (image-orientation: from-image가 기본) — 폰으로 찍은 세로 사진이 눕지 않는다.
  useEffect(() => {
    if (!isOpen || !file) return;
    // 직전 파일이 열리지 않았더라도 새 파일을 고르면 안내부터 지운다
    setLoadError(false);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      offsetRef.current = { x: 0, y: 0 };
      setZoom(1);
      setRotation(0);
      setFilterId('none');
      setLoadError(false);
      setImgReady(true);
    };
    img.onerror = () => {
      setLoadError(true);
      setImgReady(false);
    };
    img.src = url;
    return () => {
      URL.revokeObjectURL(url);
      imgRef.current = null;
      pointersRef.current.clear();
      pinchStartRef.current = null;
      setImgReady(false);
    };
  }, [isOpen, file]);

  // zoom·rotation·filter가 바뀌면 renderTo가 새로 만들어져 여기서 다시 그린다
  useEffect(() => {
    if (imgReady) draw();
  }, [imgReady, draw]);

  // 휠 확대 — React의 onWheel은 passive로 등록돼 preventDefault가 무시되므로 직접 붙인다
  // (모달 뒤 페이지가 함께 스크롤되는 것을 막아야 한다).
  useEffect(() => {
    const el = canvasRef.current;
    if (!el || !isOpen) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setZoom(prev => {
        const next = clampZoom(prev - e.deltaY * 0.0015 * prev);
        offsetRef.current = clampOffset(offsetRef.current.x, offsetRef.current.y, next);
        return next;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [isOpen, clampOffset]);

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!imgReady) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      const [a, b] = Array.from(pointersRef.current.values());
      pinchStartRef.current = { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, zoom };
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const pts = pointersRef.current;
    if (!imgReady || !pts.has(e.pointerId)) return;
    const prev = pts.get(e.pointerId)!;
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // 두 손가락: 핀치 줌 (zoom state가 바뀌면 위 effect가 다시 그린다)
    if (pts.size >= 2 && pinchStartRef.current) {
      const [a, b] = Array.from(pts.values());
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const next = clampZoom(pinchStartRef.current.zoom * (dist / pinchStartRef.current.dist));
      offsetRef.current = clampOffset(offsetRef.current.x, offsetRef.current.y, next);
      setZoom(next);
      return;
    }

    // 한 손가락·마우스: 이동. offset은 ref라 리렌더 없이 캔버스만 다시 그린다
    const rect = e.currentTarget.getBoundingClientRect();
    const k = rect.width ? VIEW / rect.width : 1;
    offsetRef.current = clampOffset(
      offsetRef.current.x + (e.clientX - prev.x) * k,
      offsetRef.current.y + (e.clientY - prev.y) * k,
      zoom
    );
    draw();
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchStartRef.current = null;
  };

  const handleZoomChange = (next: number) => {
    const z = clampZoom(next);
    offsetRef.current = clampOffset(offsetRef.current.x, offsetRef.current.y, z);
    setZoom(z);
  };

  const handleRotate = () => {
    setRotation(prev => {
      const next = (prev + 90) % 360;
      // 회전하면 덮는 방향이 바뀌어 기존 offset이 범위를 벗어날 수 있다 → 중앙으로 되돌린다
      offsetRef.current = { x: 0, y: 0 };
      return next;
    });
  };

  const handleConfirm = () => {
    const img = imgRef.current;
    if (!img) return;
    const out = document.createElement('canvas');
    out.width = OUTPUT;
    out.height = OUTPUT;
    const ctx = out.getContext('2d');
    if (!ctx) return;
    renderTo(ctx, OUTPUT);
    out.toBlob(
      blob => {
        if (blob) onConfirm(blob);
      },
      'image/jpeg',
      0.9
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title="프로필 사진 편집"
      size="md"
      closeOnOverlay={false}
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!imgReady}
            className="px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            적용
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {loadError ? (
          <div className="py-12 text-center text-sm text-slate-500">
            <p>사진을 열지 못했습니다</p>
            <p className="mt-1 text-slate-400">다른 사진을 선택해 주세요</p>
          </div>
        ) : (
          <>
            {/* 크롭 뷰포트 — 원형 바깥은 어둡게 덮어 잘릴 범위를 보여준다 */}
            <div className="relative mx-auto w-full max-w-[320px] aspect-square overflow-hidden rounded-xl bg-slate-900">
              <canvas
                ref={canvasRef}
                width={VIEW}
                height={VIEW}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                aria-label="사진 위치 조정"
                className="h-full w-full touch-none select-none cursor-grab active:cursor-grabbing"
              />
              <div
                className="pointer-events-none absolute inset-0 rounded-full shadow-[0_0_0_9999px_rgba(15,23,42,0.55)]"
                aria-hidden="true"
              />
              <div
                className="pointer-events-none absolute inset-0 rounded-full border-2 border-white/70"
                aria-hidden="true"
              />
            </div>

            <p className="text-center text-xs text-slate-500">
              끌어서 위치를 맞추고, 아래 막대로 크기를 조절하세요
            </p>

            {/* 확대·회전 */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleZoomChange(zoom - 0.2)}
                disabled={!imgReady || zoom <= MIN_ZOOM}
                aria-label="축소"
                className="shrink-0 p-2 text-slate-500 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-40"
              >
                <ZoomOut size={18} aria-hidden="true" />
              </button>
              <input
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={0.01}
                value={zoom}
                disabled={!imgReady}
                onChange={e => handleZoomChange(Number(e.target.value))}
                aria-label="사진 크기"
                className="flex-1 accent-brand-600"
              />
              <button
                type="button"
                onClick={() => handleZoomChange(zoom + 0.2)}
                disabled={!imgReady || zoom >= MAX_ZOOM}
                aria-label="확대"
                className="shrink-0 p-2 text-slate-500 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-40"
              >
                <ZoomIn size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={handleRotate}
                disabled={!imgReady}
                aria-label="90도 회전"
                className="shrink-0 p-2 text-slate-500 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-40"
              >
                <RotateCw size={18} aria-hidden="true" />
              </button>
            </div>

            {/* 사진 필터 */}
            {SUPPORTS_CANVAS_FILTER && (
              <div>
                <span className="block text-sm font-medium text-slate-700 mb-2">사진 효과</span>
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" role="group" aria-label="사진 효과">
                  {FILTERS.map(preset => {
                    const active = preset.id === filterId;
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => setFilterId(preset.id)}
                        aria-pressed={active}
                        disabled={!imgReady}
                        className={`shrink-0 px-3 py-1.5 text-sm rounded-full border transition-colors disabled:opacity-40 ${
                          active
                            ? 'bg-brand-100 border-brand-600 text-brand-700 font-medium'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
